/**
 * LayoutProvider — React Context-backed store for the chat/main central-column
 * mode and the currently active main view (see UI_TAXONOMY.md §5-6).
 *
 * Deliberately uses plain React Context + useState instead of Zustand or
 * similar — the surface is tiny (one nullable object, four actions), and
 * pulling in an external store would be over-engineering at this scale.
 * When the number of panels/views grows or we need selective re-renders,
 * this can be swapped behind the `useLayoutStore` hook boundary without
 * touching any consumers.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { MAIN_VIEWS } from './mainViews';
import { OVERLAYS } from './overlays';

interface MainViewState {
  id: string;
  props: unknown;
  expanded: boolean;
}

interface OverlayState {
  id: string;
  props: unknown;
}

interface LayoutStore {
  mainView: MainViewState | null;
  overlay: OverlayState | null;
  openMain: (id: string, props?: unknown) => void;
  expandMain: () => void;
  collapseMain: () => void;
  closeMain: () => void;
  showOverlay: (id: string, props?: unknown) => void;
  closeOverlay: () => void;
}

const LayoutContext = createContext<LayoutStore | null>(null);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [mainView, setMainView] = useState<MainViewState | null>(null);
  const [overlay, setOverlay] = useState<OverlayState | null>(null);

  const openMain = useCallback((id: string, props: unknown = undefined) => {
    const entry = MAIN_VIEWS[id];
    if (!entry) {
      console.error(`[Layout] Unknown main view: "${id}". Registered: ${Object.keys(MAIN_VIEWS).join(', ')}`);
      return;
    }
    setMainView({
      id,
      props,
      expanded: entry.defaultExpanded ?? false,
    });
  }, []);

  const expandMain = useCallback(() => {
    setMainView((v) => (v ? { ...v, expanded: true } : null));
  }, []);

  const collapseMain = useCallback(() => {
    setMainView((v) => (v ? { ...v, expanded: false } : null));
  }, []);

  const closeMain = useCallback(() => {
    setMainView(null);
  }, []);

  const showOverlay = useCallback((id: string, props: unknown = undefined) => {
    const entry = OVERLAYS[id];
    if (!entry) {
      console.error(`[Layout] Unknown overlay: "${id}". Registered: ${Object.keys(OVERLAYS).join(', ') || '(none)'}`);
      return;
    }
    setOverlay({ id, props });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlay(null);
  }, []);

  const value = useMemo<LayoutStore>(
    () => ({ mainView, overlay, openMain, expandMain, collapseMain, closeMain, showOverlay, closeOverlay }),
    [mainView, overlay, openMain, expandMain, collapseMain, closeMain, showOverlay, closeOverlay],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayoutStore(): LayoutStore {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    throw new Error('useLayoutStore must be used within <LayoutProvider>');
  }
  return ctx;
}
